import { RUNTIME_COLLECTION_FIELDS, SCHEMA_VERSION } from './schema.js';
import { dumpYaml } from './deterministicYaml.js';
import {
  slugify,
  workspaceManifestPath,
  variablesGlobalsPath,
  variablesWorkspacePath,
  environmentPath,
  collectionMetaPath,
  requestPath,
  gitignorePath,
} from './paths.js';
import { GITATTRIBUTES_TEMPLATE, GITIGNORE_TEMPLATE } from './schema.js';

function stripRuntimeFields(obj, fieldsSet) {
  if (!obj || typeof obj !== 'object') return obj;
  const out = { ...obj };
  for (const key of fieldsSet) delete out[key];
  return out;
}

/**
 * Order by an explicit `sort_order`; ties keep the order the snapshot already
 * has. The snapshot comes from SQLite in the user's own order (`sort_key`), and
 * items created in the app usually carry no `sort_order` at all — the old id
 * tie-break re-sorted those by id, so a round trip shuffled the sidebar
 * (git-sync audit 01.10.2026). Array.prototype.sort is stable.
 */
function bySortOrder(a, b) {
  const ao = typeof a?.sort_order === 'number' ? a.sort_order : 0;
  const bo = typeof b?.sort_order === 'number' ? b.sort_order : 0;
  return ao - bo;
}

/**
 * COL-06. A variable with `secret: true` is written into the TRACKED YAML with
 * its `value` omitted; the value goes to a `.local.yaml` sidecar next to it,
 * which the generated `.gitignore` has always excluded (`*.local.yaml`).
 *
 * The old serializer wrote the value in cleartext and merely tagged it
 * `secret: true`, and `git_commit_all` stages `["*"]` — so the moment a user
 * enabled the git-backed workspace and pushed, the API keys they had
 * deliberately marked secret with the Environment editor's lock icon were in
 * their repository. The `.gitignore` template named `secrets.local.yaml` and
 * `*.local.yaml`, files the serializer never wrote: an ignore rule for a file
 * that does not exist is a promise, not a mechanism. It is now both.
 *
 * `scriptAccess` (see utils/scriptVariables.js) rides along in the tracked
 * file: it is a policy bit about a variable, not a credential.
 */
function hasOptions(v) {
  return Array.isArray(v?.options) && v.options.length > 0;
}

function normalizeVariable(v) {
  if (v?.secret) {
    // A secret's alternative values (`options`, utils/variableOptions.js) are
    // as secret as its value: they go to the sidecar with it, never here.
    return {
      key: v.key ?? '',
      enabled: v.enabled !== false,
      secret: true,
      ...(v.scriptAccess ? { scriptAccess: true } : {}),
    };
  }
  return {
    key: v.key ?? '',
    value: v.value ?? '',
    enabled: v.enabled !== false,
    ...(v.scriptAccess ? { scriptAccess: true } : {}),
    // Lost on every round trip before 01.10.2026 (git-sync audit).
    ...(hasOptions(v) ? { options: v.options } : {}),
  };
}

/** `{ key, value, options? }` rows for the untracked sidecar — secrets only, omitted entirely when there are none. */
function secretSidecar(variables) {
  const secrets = (variables || [])
    .filter((v) => v?.secret && v?.key)
    .map((v) => ({ key: v.key, value: v.value ?? '', ...(hasOptions(v) ? { options: v.options } : {}) }));
  return secrets.length > 0 ? { variables: secrets } : null;
}

/**
 * COL-06 for COLLECTION and FOLDER variables (review F1, 28.09.2026): the rows
 * stay in the tracked `collection.yaml` with every field they had — so a
 * workspace without secrets keeps its exact bytes — except that a
 * `secret: true` row loses its `value`.
 */
function redactSecretRows(rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) => {
    if (!row?.secret) return row;
    // `options` hold alternative VALUES of the secret — redacted with it.
    const { value: _value, options: _options, ...rest } = row;
    return rest;
  });
}

/**
 * The untracked `collections/<slug>/collection.local.yaml` for one collection:
 * `{ id, collection_variables?: [{key, value}], folders?: [{id, variables}] }`,
 * or null when the collection holds no secret value at all.
 */
function collectionSecretSidecar(collectionId, settings, folders) {
  const colSecrets = secretSidecar(settings?.collection_variables);
  const folderSecrets = (folders || [])
    .map((f) => ({ id: f?.id, secrets: secretSidecar(f?.variables) }))
    .filter((f) => f.id != null && f.secrets)
    .map((f) => ({ id: String(f.id), variables: f.secrets.variables }));
  if (!colSecrets && folderSecrets.length === 0) return null;
  return {
    id: collectionId,
    ...(colSecrets ? { collection_variables: colSecrets.variables } : {}),
    ...(folderSecrets.length ? { folders: folderSecrets } : {}),
  };
}

/**
 * Does this parsed tracked-YAML document still carry a secret's value?
 *
 * Users who enabled git sync before this fix have those values in their commit
 * history, and rewriting someone's git history behind their back is not a fix
 * anyone asked for. So the mirror reader calls this to TELL them (see
 * `gitWorkspaceStore.loadGitSnapshot`), naming the file and the keys, and
 * leaves the decision — rotate the credential, rewrite history, both — to them.
 */
export function findExposedSecretKeys(doc) {
  const vars = Array.isArray(doc?.variables) ? doc.variables : [];
  return vars
    .filter((v) => v?.secret === true && typeof v.value === 'string' && v.value !== '')
    .map((v) => String(v.key ?? ''));
}

/**
 * Mock config is part of the lossless mirror (Phase 4 hard requirement).
 * Omit-empty: only written when it carries response examples, so requests
 * without mock data keep their historical YAML bytes. The deserializer
 * always restores a normalized `{ responseExamples: [] }` default, making
 * omit-on-write / default-on-read a clean round-trip fixed point.
 */
function mockForYaml(mock) {
  if (!mock || typeof mock !== 'object' || Array.isArray(mock)) return undefined;
  const examples = Array.isArray(mock.responseExamples) ? mock.responseExamples : [];
  if (examples.length === 0) return undefined;
  return { ...mock, responseExamples: examples };
}

/** The keys `serializeRequest` writes under its own (historical) names. */
const REQUEST_MAPPED_KEYS = new Set([
  'id', 'name', 'method', 'url', 'description', 'headers', 'params', 'cookies',
  'body_type', 'bodyType', 'body', 'auth', 'settings', 'preRequestScript',
  'pre_request_script', 'testScript', 'test_script', 'sort_order', 'folder_id', 'mock',
]);

/**
 * Never written: volatile (timestamps, server counters), positional (the
 * collection is the folder the file sits in; order is the collection index)
 * or UI-only. Everything ELSE a request carries is written as-is — the old
 * whitelist silently dropped `type` (a WebSocket / SSE / gRPC request came
 * back as HTTP), `formDataFields`, `rawBodyLanguage`, `scriptDialect`, … and
 * every field added later would have been lost the same way (git-sync audit
 * 01.10.2026). Exported for the round-trip test.
 */
export const REQUEST_DENIED_KEYS = new Set([
  'collection_id', 'collectionId', 'sort_key', 'created_at', 'updated_at', 'createdAt',
  'updatedAt', 'deleted_at', 'deletedAt', 'server_seq', 'serverSeq', 'version',
  'hasUnsavedChanges', 'isNewRequest', 'originalId', 'isDirty', '_id',
  // server-side per-user facts on records that came from the cloud (requestMappers.js)
  'can_edit', 'owner',
]);

/**
 * The editor keeps one empty row at the end of every key/value table (params,
 * headers, cookies, form data) and adds it back whenever a request opens, so
 * the row is UI, not data. Saving used to write it: an imported request,
 * changed only in its URL, then showed up on the Git page with six changed
 * fields (owner, 04.10.2026). A row counts as blank only when it holds
 * nothing at all — any key, value, description, file or a switched-off state
 * is kept.
 */
export function isBlankRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row) || !('key' in row)) return false;
  for (const [k, v] of Object.entries(row)) {
    if (k === '_id') continue;
    if (k === 'enabled') { if (v === false) return false; continue; }
    if (k === 'type') { if (v != null && v !== 'text') return false; continue; }
    if (v != null && v !== '' && v !== false) return false;
  }
  return true;
}

/** `list` without the editor's blank placeholder rows (a non-list is returned as is). */
export function withoutBlankRows(list) {
  return Array.isArray(list) ? list.filter((row) => !isBlankRow(row)) : list;
}

/** A key/value row as written: the editor's `_id` is UI state and churns diffs. */
function kvRow(row) {
  const { _id, ...rest } = row || {};
  return { ...rest, key: rest.key ?? '', value: rest.value ?? '', enabled: rest.enabled !== false };
}

/**
 * What a request carries beyond the mapped fields — `type`, the gRPC /
 * WebSocket fields, `formDataFields`, … — written as-is by both git layouts
 * (this one and the nested workspaceMirror.js).
 */
export function requestExtraFields(r, mapped = REQUEST_MAPPED_KEYS) {
  const extra = {};
  for (const [key, value] of Object.entries(r || {})) {
    if (mapped.has(key) || REQUEST_DENIED_KEYS.has(key) || value === undefined) continue;
    const kept = withoutBlankRows(value);
    // A list that held nothing but the placeholder (form data of a body-less
    // request) is not written at all; an already empty list stays as it was.
    if (Array.isArray(value) && value.length > 0 && kept.length === 0) continue;
    extra[key] = withoutRowIds(kept);
  }
  return extra;
}

/** Key/value-style rows (gRPC metadata, WebSocket headers, form fields) lose the editor's `_id` — UI state that churns diffs. */
function withoutRowIds(value) {
  if (!Array.isArray(value)) return value;
  return value.map((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row) || !('_id' in row)) return row;
    const { _id, ...rest } = row;
    return rest;
  });
}

function serializeRequest(request, requestSlug) {
  const r = stripRuntimeFields(request, new Set());
  const mock = mockForYaml(r.mock);
  const extra = requestExtraFields(r);
  return dumpYaml({
    ...extra,
    id: r.id,
    name: r.name || requestSlug,
    method: r.method || 'GET',
    url: r.url ?? '',
    description: r.description ?? '',
    headers: withoutBlankRows(r.headers || []).map(kvRow),
    params: withoutBlankRows(r.params || []).map(kvRow),
    cookies: withoutBlankRows(r.cookies || []).map(kvRow),
    // The editor writes `bodyType`; `body_type` is only what an import left
    // behind, so the editor's value wins (it used to be the other way round).
    body_type: r.bodyType ?? r.body_type ?? 'json',
    body: r.body ?? '',
    // No `auth` key means "inherit from folder / collection"
    // (utils/requestAuth.js). Defaulting it to noauth here broke inheritance
    // after every round trip.
    ...(r.auth != null ? { auth: r.auth } : {}),
    settings: r.settings ?? {},
    pre_request_script: r.preRequestScript ?? r.pre_request_script ?? '',
    test_script: r.testScript ?? r.test_script ?? '',
    sort_order: typeof r.sort_order === 'number' ? r.sort_order : 0,
    folder_id: r.folder_id ?? null,
    ...(mock ? { mock } : {}),
  });
}

function serializeCollectionMeta(collection, collectionSlug, requestIndex, position) {
  const c = stripRuntimeFields(collection, RUNTIME_COLLECTION_FIELDS);
  const settings = { ...(c.settings || {}) };
  const folders = (settings.folders || []).slice().sort(bySortOrder);
  const { folders: _f, ...settingsRest } = settings;
  if (Array.isArray(settingsRest.collection_variables)) {
    settingsRest.collection_variables = redactSecretRows(settingsRest.collection_variables);
  }
  const trackedFolders = folders.map((f) =>
    Array.isArray(f?.variables) && f.variables.some((v) => v?.secret)
      ? { ...f, variables: redactSecretRows(f.variables) }
      : f
  );

  return dumpYaml({
    id: c.id,
    name: c.name || collectionSlug,
    description: c.description ?? '',
    // Collection folders are read back in directory (alphabetical) order, so
    // the position is written down when the collection has no sort_order.
    sort_order: typeof c.sort_order === 'number' ? c.sort_order : position,
    settings: settingsRest,
    folders: trackedFolders,
    requests: requestIndex,
    ...(c.local_uid ? { local_uid: c.local_uid } : {}),
    ...(c.cloud_collection_id != null ? { cloud_collection_id: c.cloud_collection_id } : {}),
    ...(c.link_status ? { link_status: c.link_status } : {}),
    ...(c.last_synced_at ? { last_synced_at: c.last_synced_at } : {}),
  });
}

/**
 * File names for one directory's items, never two the same. `slugify` keeps 8
 * characters of the id, and ids like `dup-1727…` (a duplicated folder) share
 * them — the second file overwrote the first and a request was lost (git-sync
 * audit 01.10.2026). Only colliding items get the full id, so every other file
 * name stays exactly what it was.
 */
function uniqueSlugs(items) {
  const count = new Map();
  for (const item of items) {
    const slug = slugify(item?.name, item?.id);
    count.set(slug, (count.get(slug) || 0) + 1);
  }
  return (item) => {
    const slug = slugify(item?.name, item?.id);
    if (count.get(slug) === 1) return slug;
    const full = String(item?.id ?? '').replace(/[^a-zA-Z0-9-]/g, '');
    return `${slugify(item?.name, '').replace(/-+$/, '')}-${full}`;
  };
}

/**
 * Build a file map: relativePath -> content for a full workspace snapshot.
 * @returns {{ files: Record<string, string>, slugs: { collections: Map, requests: Map } }}
 */
export function snapshotToFileMap(snapshot, workspaceMeta = {}) {
  const files = {};
  const id = workspaceMeta.id || snapshot.id;
  const name = workspaceMeta.name || snapshot.workspaceName || 'Workspace';

  // No `selected_environment`: which environment is active is a device
  // preference — writing it dirtied the repo on every switch and a teammate's
  // pull overwrote your choice (git-sync audit 01.10.2026). Old repos still
  // carry the key; the loader keeps reading it for a fresh clone's default.
  files['apiboo.yaml'] = dumpYaml({
    version: SCHEMA_VERSION,
    id,
    name,
    storage: 'git',
    // The project this device binds the workspace to (card 2.2), so a clone
    // lands in it too (scope/projectManifest.js).
    ...(workspaceMeta.project ? { project: workspaceMeta.project } : {}),
  });

  // Apiboo's BLOCK only — the writer merges it into the user's own file
  // (schema.mergeManagedBlock), so their lines survive every flush.
  files['.gitignore'] = GITIGNORE_TEMPLATE;
  files['.gitattributes'] = GITATTRIBUTES_TEMPLATE;

  files['variables/globals.yaml'] = dumpYaml({
    variables: (snapshot.globalVariables || []).map(normalizeVariable),
  });

  files['variables/workspace.yaml'] = dumpYaml({
    variables: (snapshot.workspaceVariables || []).map(normalizeVariable),
  });

  // Untracked sidecars (`*.local.yaml`) carrying the values the tracked files
  // above deliberately omit. Written only when there is something to write, so
  // a workspace with no secrets keeps byte-identical output to before.
  const globalSecrets = secretSidecar(snapshot.globalVariables);
  if (globalSecrets) files['variables/globals.local.yaml'] = dumpYaml(globalSecrets);
  const workspaceSecrets = secretSidecar(snapshot.workspaceVariables);
  if (workspaceSecrets) files['variables/workspace.local.yaml'] = dumpYaml(workspaceSecrets);

  const collectionSlugs = new Map();
  const requestSlugs = new Map();

  const collections = (snapshot.collections || []).slice().sort(bySortOrder);
  const colSlugOf = uniqueSlugs(collections);
  for (const [position, col] of collections.entries()) {
    const colSlug = colSlugOf(col);
    collectionSlugs.set(String(col.id), colSlug);

    const requestIndex = [];
    const requests = (col.requests || []).slice().sort(bySortOrder);
    const reqSlugOf = uniqueSlugs(requests);
    for (const req of requests) {
      const reqSlug = reqSlugOf(req);
      requestSlugs.set(`${col.id}:${req.id}`, reqSlug);
      requestIndex.push({ id: req.id, file: `requests/${reqSlug}.yaml` });
      files[`collections/${colSlug}/requests/${reqSlug}.yaml`] = serializeRequest(req, reqSlug);
    }

    files[`collections/${colSlug}/collection.yaml`] = serializeCollectionMeta(
      col,
      colSlug,
      requestIndex,
      position
    );
    const colSecrets = collectionSecretSidecar(col.id, col.settings, col.settings?.folders);
    if (colSecrets) files[`collections/${colSlug}/collection.local.yaml`] = dumpYaml(colSecrets);
  }

  const environments = (snapshot.environments || []).slice().sort(bySortOrder);
  const envSlugOf = uniqueSlugs(environments);
  for (const [position, env] of environments.entries()) {
    const envSlug = envSlugOf(env);
    files[`environments/${envSlug}.yaml`] = dumpYaml({
      id: env.id,
      name: env.name || envSlug,
      variables: (env.variables || []).map(normalizeVariable),
      // Ordering must round-trip (env files are re-read in directory order).
      // Environments made in the app carry no sort_order, so the position is
      // written instead — before 01.10.2026 they came back sorted by id.
      sort_order: typeof env.sort_order === 'number' ? env.sort_order : position,
    });
    const envSecrets = secretSidecar(env.variables);
    if (envSecrets) {
      files[`environments/${envSlug}.local.yaml`] = dumpYaml({ id: env.id, ...envSecrets });
    }
  }

  // Smart Mock schema tree (folders + items) — part of the lossless mirror.
  // Single deterministic tree file, matching the layout gitWorkspaceStore has
  // always read back (schemas/tree.yaml), so existing repos stay byte-stable.
  if (snapshot.schemas && typeof snapshot.schemas === 'object') {
    files['schemas/tree.yaml'] = dumpYaml({
      folders: Array.isArray(snapshot.schemas.folders) ? snapshot.schemas.folders : [],
      items: Array.isArray(snapshot.schemas.items) ? snapshot.schemas.items : [],
    });
  }

  return { files, slugs: { collections: collectionSlugs, requests: requestSlugs } };
}

export {
  workspaceManifestPath,
  variablesGlobalsPath,
  variablesWorkspacePath,
  environmentPath,
  collectionMetaPath,
  requestPath,
  gitignorePath,
  slugify,
};
